import {
  MESSAGE_TYPES,
  type WidgetMessage,
  type TrackEventPayload,
  type TrackErrorPayload,
  type QuickPromptPayload,
  type OpenConversationPayload,
  isValidWidgetMessage,
} from "../shared/messaging";
import {
  type WidgetConfig,
  type QuickPromptConfig,
  type AgentConfig,
  FabSize,
  PermissionPolicies,
  type PermissionPolicy,
} from "../shared/types";
import { DEFAULT_ICON_DATA_URI } from "../shared/constants";
import { matchAllowedOrigin, isHiddenPath } from "./validation";
import { isValidConversationMessage, isValidGuid } from "../shared/validation";
import { sanitizeAgentMessageHtml } from "../widget/utils/sanitizeAgentMessageHtml";

const WIDGET_CDN_ORIGIN = (() => {
  const widgetCdnUrl = import.meta.env.VITE_WIDGET_CDN_URL;
  if (!widgetCdnUrl) return null;
  try {
    return new URL(widgetCdnUrl).origin;
  } catch {
    return null;
  }
})();

interface LoaderState {
  isLoaded: boolean;
  isQuickPromptReady: boolean;
  isOpen: boolean;
  iframe: HTMLIFrameElement | null;
  triggerButton: HTMLButtonElement | null;
  calloutElement: HTMLDivElement | null;
  isCalloutConsumed: boolean;
  agentConfig: AgentConfig | null;
  widgetConfig: WidgetConfig | null;
}

interface LoadOptions {
  suppressInitialCallout?: boolean;
}

interface OpenConversationConfig {
  conversationId: string;
  messages: OpenConversationPayload["messages"];
  agentId?: string;
  apiUrl?: string;
}

const state: LoaderState = {
  isLoaded: false,
  isQuickPromptReady: false,
  isOpen: false,
  iframe: null,
  triggerButton: null,
  calloutElement: null,
  isCalloutConsumed: false,
  agentConfig: null,
  widgetConfig: null,
};

const QUICK_PROMPT_REQUIRED_ERROR =
  "agentId, apiUrl, and prompt are required when the widget is not loaded";
const OPEN_CONVERSATION_REQUIRED_ERROR =
  "agentId and apiUrl are required when the widget is not loaded";
const QUICK_PROMPT_PROMPT_REQUIRED_ERROR = "prompt is required";
const QUICK_PROMPT_WIDGET_UNAVAILABLE_ERROR = "Widget is not available";
const OPEN_CONVERSATION_WIDGET_UNAVAILABLE_ERROR = "Widget is not available";
const QUICK_PROMPT_READY_TIMEOUT_MS = 10000;

function sendTelemetryEvent(
  eventName: string,
  attributes?: Record<string, string | number | boolean>,
): void {
  if (!state.iframe?.contentWindow) return;

  const payload: TrackEventPayload = {
    eventName,
    attributes,
  };

  const iframeOrigin = new URL(state.iframe.src).origin;
  state.iframe.contentWindow.postMessage(
    {
      type: MESSAGE_TYPES.TRACK_EVENT,
      payload,
    },
    iframeOrigin,
  );
}

function sendTelemetryError(
  error: unknown,
  attributes?: Record<string, string | number | boolean>,
): void {
  if (!state.iframe?.contentWindow) return;

  const errorObj = error instanceof Error ? error : new Error(String(error));
  const payload: TrackErrorPayload = {
    error: {
      message: errorObj.message,
      stack: errorObj.stack,
    },
    attributes,
  };

  const iframeOrigin = new URL(state.iframe.src).origin;
  state.iframe.contentWindow.postMessage(
    {
      type: MESSAGE_TYPES.TRACK_ERROR,
      payload,
    },
    iframeOrigin,
  );
}

function validateConfig(config: Partial<WidgetConfig>): config is WidgetConfig {
  if (!config.agentId) {
    console.error("[ResidentAgent Widget] agentId is required");
    return false;
  }
  if (!config.apiUrl) {
    console.error("[ResidentAgent Widget] apiUrl is required");
    return false;
  }
  return true;
}

function hasText(value: string | undefined): value is string {
  return typeof value === "string" && value.trim().length > 0;
}

function getCalloutText(agentConfig: AgentConfig | null): string | null {
  const callout = agentConfig?.fabCallout;
  if (typeof callout !== "string") return null;
  const sanitized = sanitizeAgentMessageHtml(callout);
  const container = document.createElement("div");
  container.innerHTML = sanitized;
  return hasText(container.textContent ?? "") ? sanitized : null;
}

const ALLOWED_ICON_PROTOCOLS = new Set<string>(["https:", "http:", "data:"]);

function sanitizeIconUrl(iconUrl: string | undefined): string {
  if (!iconUrl) {
    return DEFAULT_ICON_DATA_URI;
  }

  try {
    const url = new URL(iconUrl, window.location.origin);
    if (ALLOWED_ICON_PROTOCOLS.has(url.protocol)) {
      return url.href;
    }
  } catch {
    console.warn(
      "[ResidentAgent Widget] Invalid icon URL, using default:",
      iconUrl,
    );
  }

  return DEFAULT_ICON_DATA_URI;
}

function isDataUri(url: string): boolean {
  return url.startsWith("data:");
}

function decodeSvgDataUri(dataUri: string): string | null {
  const commaIndex = dataUri.indexOf(",");
  if (commaIndex === -1) return null;
  const header = dataUri.slice(0, commaIndex);
  const payload = dataUri.slice(commaIndex + 1);

  const mimeMatch = header.match(/^data:([^;,]+)/);
  const mimeType = mimeMatch ? mimeMatch[1] : "";
  if (!mimeType.includes("svg")) return null;

  const isBase64 = header.endsWith(";base64");
  return isBase64 ? atob(payload) : decodeURIComponent(payload);
}

function createIconElement(iconUrl: string): HTMLDivElement {
  const container = document.createElement("div");
  container.className = "resident-agent-trigger-icon-container";

  const sanitizedUrl = sanitizeIconUrl(iconUrl);

  const svgMarkup = isDataUri(sanitizedUrl)
    ? decodeSvgDataUri(sanitizedUrl)
    : null;

  if (svgMarkup) {
    const wrapper = document.createElement("div");
    wrapper.className = "resident-agent-trigger-icon";
    wrapper.innerHTML = svgMarkup;
    const svg = wrapper.querySelector("svg");
    if (svg) {
      svg.setAttribute("aria-hidden", "true");
      svg.style.width = "100%";
      svg.style.height = "100%";
    }
    container.appendChild(wrapper);
  } else {
    const img = document.createElement("img");
    img.className = "resident-agent-trigger-icon";
    img.alt = "";
    img.src = sanitizedUrl;
    container.appendChild(img);
  }

  return container;
}

async function fetchAgentConfig(
  apiUrl: string,
  agentId: string,
): Promise<AgentConfig> {
  try {
    const response = await fetch(
      `${apiUrl}/api/v1/public/agents/${agentId}/configuration`,
    );
    if (!response.ok) {
      throw new Error(`Failed to fetch agent config (HTTP ${response.status})`);
    }
    return await response.json();
  } catch (error) {
    if (error instanceof Error) {
      throw error;
    }
    throw new Error("Failed to fetch agent config");
  }
}

const DIALOG_CONTAINER_ID = "resident-agent-widget-dialog";
const DEFAULT_FAB_BOTTOM_OFFSET = 0;
const ALLOWED_FAB_BOTTOM_OFFSETS = new Set([0, 12, 24, 36, 48, 60, 72, 84, 96]);

function resolveFabBottomOffset(agentConfig: AgentConfig): number {
  const offset = agentConfig.fabBottomOffset;
  if (
    typeof offset === "number" &&
    Number.isInteger(offset) &&
    ALLOWED_FAB_BOTTOM_OFFSETS.has(offset)
  ) {
    return offset;
  }

  return DEFAULT_FAB_BOTTOM_OFFSET;
}

function createTriggerButton(agentConfig: AgentConfig): HTMLButtonElement {
  const button = document.createElement("button");
  button.setAttribute("aria-label", "Open chat");
  button.setAttribute("aria-expanded", "false");
  // aria-controls points to the dialog container so AT users know what this
  // button controls. The dedicated close button lives inside the
  // cross-origin iframe, not in loader.js.
  button.setAttribute("aria-controls", DIALOG_CONTAINER_ID);
  button.className = "resident-agent-trigger";
  button.id = "resident-agent-trigger-button";

  const primaryColor = agentConfig?.primaryColor || "#CC0020";
  const iconUrl = agentConfig?.iconUrl || DEFAULT_ICON_DATA_URI;
  const position = agentConfig?.position || "BottomRight";
  const isLeft = position === "BottomLeft";
  const size = agentConfig?.fabSize || FabSize.Medium;
  const fabBottomOffset = resolveFabBottomOffset(agentConfig);
  const triggerBottom = 20 + fabBottomOffset;
  const iframeBottom = 90 + fabBottomOffset;
  const iframeMaxHeightOffset = 120 + fabBottomOffset;
  const sizeStyles: Record<FabSize, { width: string; height: string }> = {
    [FabSize.Small]: { width: "40px", height: "40px" },
    [FabSize.Medium]: { width: "60px", height: "60px" },
    [FabSize.Large]: { width: "80px", height: "80px" },
  };

  const iconElement = createIconElement(iconUrl);
  button.appendChild(iconElement);

  button.style.setProperty("--trigger-color", primaryColor);

  const removeBackground = agentConfig?.removeBackground === true;

  const styles = `
    .resident-agent-trigger {
      position: fixed;
      bottom: ${triggerBottom}px;
      ${isLeft ? "left" : "right"}: 20px;
      width: ${sizeStyles[size].width};
      height: ${sizeStyles[size].height};
      border-radius: 13px;
      border: none;
      padding: 8px;
      cursor: pointer;
      z-index: 9998;
      display: flex;
      align-items: center;
      justify-content: center;
      color: white;
      background: transparent;
      transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border 0.2s ease;
      overflow: hidden;
    }

    .resident-agent-trigger,
    .resident-agent-callout {
      --resident-agent-glass-border: 1px solid rgba(255, 255, 255, 0.3);
      --resident-agent-callout-border: 1px solid rgba(255, 255, 255, 0.4);
    }

    @supports (backdrop-filter: blur(10px)) or (-webkit-backdrop-filter: blur(10px)) {
      .resident-agent-trigger {
        background: color-mix(in srgb, var(--trigger-color, #CC0020) 30%, rgba(255, 255, 255, 0.7));
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        border: 1px solid rgba(255, 255, 255, 0.3);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
      }
      
      .resident-agent-trigger:hover {
        background: color-mix(in srgb, var(--trigger-color, #CC0020) 40%, rgba(255, 255, 255, 0.7));
        border-color: rgba(255, 255, 255, 0.4);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
      }
      
      .resident-agent-trigger.open {
        background: var(--trigger-color, #CC0020);
        backdrop-filter: none;
        -webkit-backdrop-filter: none;
        border-color: transparent;
      }

      .resident-agent-callout {
        background: color-mix(in srgb, var(--trigger-color, #CC0020) 30%, rgba(255, 255, 255, 0.7));
        backdrop-filter: blur(8px);
        -webkit-backdrop-filter: blur(8px);
        border: var(--resident-agent-callout-border);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
      }
    }

    @supports not ((backdrop-filter: blur(10px)) or (-webkit-backdrop-filter: blur(10px))) {
      .resident-agent-trigger {
        background: color-mix(in srgb, var(--trigger-color, #CC0020) 30%, white);
        border: 1px solid rgba(0, 0, 0, 0.1);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
      }
      
      .resident-agent-trigger:hover {
        background: color-mix(in srgb, var(--trigger-color, #CC0020) 40%, white);
        border-color: rgba(0, 0, 0, 0.15);
        box-shadow: 0 6px 20px rgba(0, 0, 0, 0.15);
      }
      
      .resident-agent-trigger.open {
        background: var(--trigger-color, #CC0020);
        border-color: transparent;
      }

      .resident-agent-callout {
        background: color-mix(in srgb, var(--trigger-color, #CC0020) 30%, white);
        border: 1px solid rgba(0, 0, 0, 0.1);
        box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
      }
    }

    .resident-agent-trigger-icon-container {
      width: 100%;
      height: 100%;
      display: flex;
      align-items: center;
      justify-content: center;
      position: relative;
      z-index: 1;
    }

    .resident-agent-trigger-icon {
      width: 100%;
      height: 100%;
      object-fit: contain;
      border-radius: 0;
      opacity: 1;
    }

    .resident-agent-trigger:hover {
      transform: scale(1.05);
    }

    .resident-agent-trigger:focus {
      outline: 2px solid var(--trigger-color, #CC0020);
      outline-offset: 2px;
    }

    .resident-agent-trigger:active {
      transform: scale(0.95);
    }

    .resident-agent-callout {
      position: fixed;
      bottom: calc(20px + ${sizeStyles[size].height} + ${fabBottomOffset}px + 4px);
      ${isLeft ? "left" : "right"}: 20px;
      box-sizing: border-box;
      max-width: min(208px, calc(100vw - 40px));
      padding: 12px 4px 12px 14px;
      border: var(--resident-agent-callout-border);
      border-radius: 12px;
      background: color-mix(in srgb, var(--trigger-color, #CC0020) 30%, rgba(255, 255, 255, 0.7));
      box-shadow: 0 8px 28px rgba(15, 23, 42, 0.18);
      color: white;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 14px;
      line-height: 1.35;
      z-index: 9997;
      display: flex;
      align-items: center;
      overflow-wrap: anywhere;
    }

    .resident-agent-callout-text {
      position: relative;
      z-index: 1;
    }

    .resident-agent-callout-text p {
      margin: 0 0 0.6em;
      white-space: pre-wrap;
    }

    .resident-agent-callout-text p:last-child {
      margin-bottom: 0;
    }

    .resident-agent-callout-text a {
      color: inherit;
      text-decoration: underline;
      text-underline-offset: 2px;
    }

    .resident-agent-callout-text a:hover,
    .resident-agent-callout-text a:visited {
      color: inherit;
    }

    .resident-agent-callout-text a:focus-visible {
      outline: 2px solid #ffffff;
      outline-offset: 2px;
      border-radius: 2px;
    }

    .resident-agent-callout.hidden {
      display: none;
    }

    .resident-agent-callout-close {
      position: absolute;
      top: -12px;
      right: -12px;
      width: 24px;
      height: 24px;
      border: solid 1px #374151;
      border-radius: 4px;
      background: #ffffff;
      color: #374151;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: Arial, Helvetica, sans-serif;
      font-size: 20px;
      font-weight: 400;
      line-height: 1;
      padding: 0;
      transition: background-color 0.2s ease;
      z-index: 2;
    }

    .resident-agent-callout-close:hover {
      background: rgba(255, 255, 255, 0.9);
      color: #374151;
    }

    .resident-agent-callout-close:focus {
      outline: 2px solid var(--trigger-color, #CC0020);
      outline-offset: 1px;
    }

    .resident-agent-callout-close-icon {
      display: block;
    }

    .resident-agent-iframe-container {
      position: fixed;
      bottom: ${iframeBottom}px;
      ${isLeft ? "left" : "right"}: 20px;
      width: min(500px, calc(100vw - 40px));
      height: 900px;
      max-height: calc(100vh - ${iframeMaxHeightOffset}px);
      border: none;
      border-radius: 12px;
      box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
      z-index: 9999;
      display: none;
      overflow: hidden;
    }

    .resident-agent-iframe-container.open {
      display: block;
    }

    @media (max-width: 768px) {
      .resident-agent-trigger {
        bottom: calc(env(safe-area-inset-bottom, 0px) + ${fabBottomOffset}px + 16px);
        ${isLeft ? "left" : "right"}: calc(env(safe-area-inset-${isLeft ? "left" : "right"}, 0px) + 16px);
      }

      .resident-agent-callout {
        bottom: calc(env(safe-area-inset-bottom, 0px) + ${fabBottomOffset}px + 16px + ${sizeStyles[size].height} + 4px);
        ${isLeft ? "left" : "right"}: calc(env(safe-area-inset-${isLeft ? "left" : "right"}, 0px) + 16px);
        max-width: min(208px, calc(100vw - 32px));
      }

      .resident-agent-iframe-container {
        position: fixed;
        top: calc(12px + var(--viewport-offset-top, 0px));
        right: 12px;
        left: 12px;
        height: calc(var(--mobile-vh, 100vh) - 24px - ${fabBottomOffset}px);
        border-radius: 12px;
        overflow: hidden;
      }
    }

    .resident-agent-iframe {
      width: 100%;
      height: 100%;
      border: none;
      background: transparent;
    }
    @supports (backdrop-filter: blur(10px)) or (-webkit-backdrop-filter: blur(10px)) {
      .resident-agent-trigger::before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(135deg, rgb(135 135 135 / 50%) 0%, rgb(66 66 66 / 70%) 100%);
        opacity: 1;
        transition: opacity 0.3s ease;
        z-index: 0;
        border-radius: inherit;
        pointer-events: none;
      }

      .resident-agent-callout::before {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(135deg, rgb(135 135 135 / 50%) 0%, rgb(66 66 66 / 70%) 100%);
        opacity: 1;
        transition: opacity 0.3s ease;
        z-index: 0;
        border-radius: inherit;
        pointer-events: none;
      }

      .resident-agent-trigger {
        backdrop-filter: blur(8px) saturate(3.5);
        -webkit-backdrop-filter: blur(15px);
        border: var(--resident-agent-glass-border);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4), inset 0 -1px 0 rgba(255, 255, 255, 0.1);
      }

      .resident-agent-callout {
        backdrop-filter: blur(8px) saturate(3.5);
        -webkit-backdrop-filter: blur(15px);
        border: var(--resident-agent-callout-border);
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.4), inset 0 -1px 0 rgba(255, 255, 255, 0.1);
      }

      .resident-agent-trigger::after {
        content: '';
        position: absolute;
        inset: 0;
        background: linear-gradient(
          135deg,
          color-mix(in srgb, var(--trigger-color, #CC0020) 50%, transparent) 0%,
          color-mix(in srgb, var(--trigger-color, #CC0020) 80%, transparent) 100%
        );
        opacity: 0;
        transition: opacity 0.3s ease;
        z-index: 0;
        border-radius: inherit;
        pointer-events: none;
      }
    }

    ${
      removeBackground
        ? `
    /* Overrides placed last so they win the cascade over the glass rules above without touching them. */
    .resident-agent-trigger,
    .resident-agent-trigger:hover,
    .resident-agent-trigger.open {
      background: transparent;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      border: none;
      box-shadow: none;
    }

    .resident-agent-trigger::before,
    .resident-agent-trigger::after {
      display: none;
    }

    .resident-agent-callout {
      background: #FFFFFF;
      color: #000000;
      backdrop-filter: none;
      -webkit-backdrop-filter: none;
      border: 1px solid #000000;
      box-shadow: 0 8px 28px rgba(15, 23, 42, 0.5);
    }

    .resident-agent-callout::before {
      display: none;
    }
    `
        : ""
    }
  `;

  if (!document.getElementById("resident-agent-styles")) {
    const styleEl = document.createElement("style");
    styleEl.id = "resident-agent-styles";
    styleEl.textContent = styles;
    document.head.appendChild(styleEl);
  }

  return button;
}

function createCalloutElement(agentConfig: AgentConfig): HTMLDivElement | null {
  const calloutText = getCalloutText(agentConfig);
  if (!calloutText) return null;

  const callout = document.createElement("div");
  callout.className = "resident-agent-callout";
  callout.setAttribute("role", "note");
  callout.style.setProperty(
    "--trigger-color",
    agentConfig.primaryColor || "#CC0020",
  );

  const text = document.createElement("span");
  text.className = "resident-agent-callout-text";
  text.innerHTML = calloutText;

  const closeButton = document.createElement("button");
  closeButton.type = "button";
  closeButton.className = "resident-agent-callout-close";
  closeButton.setAttribute("aria-label", "Dismiss greeting");
  closeButton.innerHTML =
    '<svg class="resident-agent-callout-close-icon" width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M12 4L4 12M4 4L12 12" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  closeButton.addEventListener("click", () => {
    consumeCallout();
  });

  callout.append(text, closeButton);
  return callout;
}

function syncCalloutVisibility(): void {
  if (!state.calloutElement) return;

  const shouldHide =
    state.isOpen ||
    state.isCalloutConsumed ||
    !getCalloutText(state.agentConfig);
  state.calloutElement.classList.toggle("hidden", shouldHide);
}

function consumeCallout(): void {
  state.isCalloutConsumed = true;
  syncCalloutVisibility();
}

const VALID_PERMISSION_POLICIES = new Set<PermissionPolicy>(
  Object.values(PermissionPolicies),
);

export function resolvePermissionPolicies(agentConfig: AgentConfig): string {
  const fallback = PermissionPolicies.ClipboardWrite;

  if (!Array.isArray(agentConfig.permissionPolicies)) {
    return fallback;
  }

  const valid = agentConfig.permissionPolicies.filter((p) =>
    VALID_PERMISSION_POLICIES.has(p as PermissionPolicy),
  );

  if (valid.length === 0) {
    return fallback;
  }

  return valid.join("; ");
}

function createIframe(
  config: WidgetConfig,
  agentConfig: AgentConfig,
): HTMLIFrameElement {
  const container = document.createElement("div");
  container.className = "resident-agent-iframe-container";
  container.id = DIALOG_CONTAINER_ID;
  container.setAttribute("role", "dialog");
  // aria-modal is intentionally omitted: this is a cross-origin iframe and we
  // cannot inert the host-page background, so the semantic would be misleading
  // to assistive technology (WCAG 4.1.2).
  // aria-label is used instead of aria-labelledby because the heading lives
  // inside a cross-origin iframe and cannot be referenced from the host page.
  container.setAttribute("aria-label", "Chat");

  const iframe = document.createElement("iframe");
  iframe.className = "resident-agent-iframe";
  iframe.setAttribute("title", "Chat Widget");
  iframe.setAttribute("allow", resolvePermissionPolicies(agentConfig));

  const params = new URLSearchParams({
    agentId: config.agentId,
    apiUrl: config.apiUrl,
    parentOrigin: window.location.origin,
    sourceUrl: window.location.href,
  });

  const widgetBaseUrl = import.meta.env.VITE_WIDGET_CDN_URL;
  if (!widgetBaseUrl) {
    throw new Error(
      "[ResidentAgent Widget] VITE_WIDGET_CDN_URL environment variable is required",
    );
  }
  iframe.src = `${widgetBaseUrl}/widget.html?${params.toString()}`;

  container.appendChild(iframe);
  document.body.appendChild(container);

  return iframe;
}

function shouldOpenWidgetOnLoad(config: WidgetConfig): boolean {
  if (config.openOnLoad != null) {
    return config.openOnLoad;
  }
  const agentWidgetOpen = new URLSearchParams(window.location.search).get(
    "agentWidgetOpen",
  );
  return agentWidgetOpen === "true" || agentWidgetOpen === "1";
}

function handleEscapeKey(event: KeyboardEvent) {
  if (event.key === "Escape" && state.isOpen) {
    toggleWidget();
    state.triggerButton?.focus();
  }
}

function toggleWidget() {
  if (!state.iframe || !state.triggerButton) return;

  state.isOpen = !state.isOpen;
  if (state.isOpen) {
    consumeCallout();
  }

  const container = state.iframe.parentElement;
  if (container) {
    container.classList.toggle("open", state.isOpen);
    state.triggerButton.classList.toggle("open", state.isOpen);
  }

  state.triggerButton.setAttribute("aria-expanded", String(state.isOpen));
  state.triggerButton.setAttribute(
    "aria-label",
    state.isOpen ? "Close chat" : "Open chat",
  );

  if (state.isOpen) {
    // Move focus into the iframe when the widget opens.
    // tabindex="0" makes the iframe element itself focusable.
    state.iframe.setAttribute("tabindex", "0");
    requestAnimationFrame(() => {
      if (state.isOpen && state.iframe?.isConnected) {
        state.iframe.focus();
      }
    });
    // Close on Escape.
    document.addEventListener("keydown", handleEscapeKey);
  } else {
    document.removeEventListener("keydown", handleEscapeKey);
  }

  syncCalloutVisibility();

  const triggerAttributes: Record<string, string> = {
    action: state.isOpen ? "open" : "close",
    sourceUrl: window.location.href,
  };
  if (state.widgetConfig?.agentId) {
    triggerAttributes.agentId = state.widgetConfig.agentId;
  }
  if (state.agentConfig?.organizationId) {
    triggerAttributes.organizationId = state.agentConfig.organizationId;
  }
  sendTelemetryEvent("trigger.clicked", triggerAttributes);
}

function handleMessage(event: MessageEvent<unknown>) {
  if (!state.iframe) return;

  const iframeOrigin = new URL(state.iframe.src).origin;
  if (event.origin !== iframeOrigin) return;

  if (!isValidWidgetMessage(event.data)) {
    console.warn(
      "[ResidentAgent Widget] Ignoring invalid message:",
      event.data,
    );
    return;
  }

  const { type, payload } = event.data;

  switch (type) {
    case MESSAGE_TYPES.CLOSE_WIDGET:
      if (state.isOpen) {
        toggleWidget();
        state.triggerButton?.focus();
      }
      break;
    case MESSAGE_TYPES.RESIZE_WIDGET:
      break;
    case MESSAGE_TYPES.QUICK_PROMPT_READY:
      state.isQuickPromptReady = true;
      break;
    case MESSAGE_TYPES.ERROR:
      console.error("[ResidentAgent Widget]", payload);
      sendTelemetryError(new Error(String(payload)), {
        source: "widget_iframe",
      });
      break;
  }
}

function updateMobileHeight(): void {
  if (window.visualViewport) {
    const vh = window.visualViewport.height;
    const offsetTop = window.visualViewport.offsetTop;

    document.documentElement.style.setProperty("--mobile-vh", `${vh}px`);
    document.documentElement.style.setProperty(
      "--viewport-offset-top",
      `${offsetTop}px`,
    );
  }
}

function setupMobileViewportHandling(): void {
  if (window.visualViewport) {
    updateMobileHeight();
    window.visualViewport.addEventListener("resize", updateMobileHeight);
  }
}

function clearState(): void {
  if (state.triggerButton) {
    state.triggerButton.removeEventListener("click", toggleWidget);
    state.triggerButton.remove();
  }
  if (state.calloutElement) {
    state.calloutElement.remove();
  }
  if (state.iframe) {
    const container = state.iframe.parentElement;
    if (container) {
      container.remove();
    }
  }
  const styleEl = document.getElementById("resident-agent-styles");
  if (styleEl) {
    styleEl.remove();
  }
  window.removeEventListener("message", handleMessage);
  document.removeEventListener("keydown", handleEscapeKey);
  if (window.visualViewport) {
    window.visualViewport.removeEventListener("resize", updateMobileHeight);
  }
  state.triggerButton = null;
  state.calloutElement = null;
  state.iframe = null;
  state.agentConfig = null;
  state.widgetConfig = null;
  state.isLoaded = false;
  state.isQuickPromptReady = false;
  state.isOpen = false;
  state.isCalloutConsumed = false;
}

async function load(
  config: Partial<WidgetConfig>,
  options: LoadOptions = {},
): Promise<void> {
  if (!validateConfig(config)) {
    throw new Error("Invalid widget configuration");
  }

  if (state.triggerButton) {
    throw new Error("Widget already loaded. Call reset() first.");
  }

  try {
    state.widgetConfig = config;
    state.agentConfig = await fetchAgentConfig(config.apiUrl, config.agentId);

    const currentOrigin = window.location.origin;
    const currentPath = window.location.pathname;

    if (WIDGET_CDN_ORIGIN && currentOrigin === WIDGET_CDN_ORIGIN) {
      console.info(
        `[ResidentAgent Widget] Skipping domain checks: current origin matches widget CDN origin "${WIDGET_CDN_ORIGIN}"`,
      );
    } else {
      const allowedOriginEntry = matchAllowedOrigin(
        currentOrigin,
        state.agentConfig.allowedOrigins,
      );

      if (!allowedOriginEntry) {
        console.info(
          `[ResidentAgent Widget] Widget blocked: origin "${currentOrigin}" is not in allowedOrigins`,
        );
        return;
      }

      if (isHiddenPath(currentPath, allowedOriginEntry.hiddenPaths)) {
        console.info(
          `[ResidentAgent Widget] Widget blocked: path "${currentPath}" matches hiddenPaths for origin "${currentOrigin}"`,
        );
        return;
      }
    }

    const shouldAutoOpen = shouldOpenWidgetOnLoad(config);
    state.isCalloutConsumed =
      options.suppressInitialCallout === true || shouldAutoOpen;

    state.triggerButton = createTriggerButton(state.agentConfig);
    state.triggerButton.addEventListener("click", toggleWidget);
    document.body.appendChild(state.triggerButton);

    state.calloutElement = createCalloutElement(state.agentConfig);
    if (state.calloutElement) {
      document.body.appendChild(state.calloutElement);
      syncCalloutVisibility();
    }

    state.iframe = createIframe(config, state.agentConfig);

    setupMobileViewportHandling();

    await new Promise<void>((resolve, reject) => {
      const timeout = setTimeout(() => {
        reject(
          new Error("Widget iframe failed to initialize within 10 seconds"),
        );
      }, 10000);

      const handleReady = (event: MessageEvent) => {
        if (!state.iframe) return;

        const iframeOrigin = new URL(state.iframe.src).origin;
        if (event.origin !== iframeOrigin) return;

        if (
          isValidWidgetMessage(event.data) &&
          event.data.type === MESSAGE_TYPES.WIDGET_READY
        ) {
          clearTimeout(timeout);
          window.removeEventListener("message", handleReady);
          state.isLoaded = true;
          resolve();
        }
      };

      window.addEventListener("message", handleReady);
    });

    window.addEventListener("message", handleMessage);

    const attributes: Record<string, string> = {
      agentId: config.agentId,
      sourceUrl: window.location.href,
    };
    if (state.agentConfig?.organizationId) {
      attributes.organizationId = state.agentConfig.organizationId;
    }
    sendTelemetryEvent("widget.loaded", attributes);

    if (shouldAutoOpen) {
      toggleWidget();
    }
  } catch (error) {
    if (state.iframe?.contentWindow) {
      sendTelemetryError(error, {
        source: "widget_load",
        agentId: config.agentId,
      });
    } else {
      console.error(
        "[ResidentAgent Widget] Load failed before telemetry available:",
        error,
      );
    }
    clearState();
    throw error;
  }
}

function openWidgetIfClosed(): void {
  if (!state.isOpen) {
    toggleWidget();
  }
}

function isQuickPromptTargetAvailable(): boolean {
  return (
    state.isLoaded &&
    state.iframe?.isConnected === true &&
    state.iframe.contentWindow != null &&
    state.triggerButton?.isConnected === true
  );
}

function hasWidgetDom(): boolean {
  return state.iframe != null || state.triggerButton != null;
}

function postOpenConversation(payload: OpenConversationPayload): void {
  if (!state.iframe?.contentWindow) {
    throw new Error(OPEN_CONVERSATION_WIDGET_UNAVAILABLE_ERROR);
  }

  const iframeOrigin = new URL(state.iframe.src).origin;
  state.iframe.contentWindow.postMessage(
    {
      type: MESSAGE_TYPES.OPEN_CONVERSATION,
      payload,
    },
    iframeOrigin,
  );
}

function postQuickPrompt(prompt: string): void {
  if (!state.iframe?.contentWindow) {
    throw new Error(QUICK_PROMPT_WIDGET_UNAVAILABLE_ERROR);
  }

  const payload: QuickPromptPayload = { prompt };
  const iframeOrigin = new URL(state.iframe.src).origin;
  state.iframe.contentWindow.postMessage(
    {
      type: MESSAGE_TYPES.QUICK_PROMPT,
      payload,
    },
    iframeOrigin,
  );
}

async function waitForQuickPromptReady(): Promise<void> {
  if (state.isQuickPromptReady) {
    return;
  }

  if (!state.iframe) {
    throw new Error(QUICK_PROMPT_WIDGET_UNAVAILABLE_ERROR);
  }

  const iframeOrigin = new URL(state.iframe.src).origin;

  await new Promise<void>((resolve, reject) => {
    let timeout: ReturnType<typeof setTimeout>;

    const cleanup = () => {
      clearTimeout(timeout);
      window.removeEventListener("message", handleReady);
    };

    timeout = setTimeout(() => {
      cleanup();
      reject(
        new Error(
          "Widget quick prompt handler failed to initialize within 10 seconds",
        ),
      );
    }, QUICK_PROMPT_READY_TIMEOUT_MS);

    const handleReady = (event: MessageEvent<unknown>) => {
      if (event.origin !== iframeOrigin) return;

      if (
        isValidWidgetMessage(event.data) &&
        event.data.type === MESSAGE_TYPES.QUICK_PROMPT_READY
      ) {
        state.isQuickPromptReady = true;
        cleanup();
        resolve();
      }
    };

    window.addEventListener("message", handleReady);
  });
}

async function openConversationAsync(
  config: OpenConversationConfig,
): Promise<void> {
  if (
    !hasText(config.conversationId) ||
    !isValidGuid(config.conversationId.trim()) ||
    !Array.isArray(config.messages) ||
    config.messages.length === 0 ||
    !config.messages.every(isValidConversationMessage)
  ) {
    throw new Error("conversationId and valid handoff messages are required");
  }

  if (!isQuickPromptTargetAvailable()) {
    if (!hasText(config.agentId) || !hasText(config.apiUrl)) {
      throw new Error(OPEN_CONVERSATION_REQUIRED_ERROR);
    }

    if (hasWidgetDom()) {
      clearState();
    }

    await load(
      {
        agentId: config.agentId,
        apiUrl: config.apiUrl,
      },
      { suppressInitialCallout: true },
    );
  }

  if (!isQuickPromptTargetAvailable()) {
    throw new Error(QUICK_PROMPT_WIDGET_UNAVAILABLE_ERROR);
  }

  openWidgetIfClosed();
  const handoff = {
    conversationId: config.conversationId,
    messages: config.messages,
  };

  // load() resolves only after the iframe has installed its parent message listener.
  postOpenConversation(handoff);
}

async function quickPromptAsync(
  config: Partial<QuickPromptConfig> = {},
): Promise<void> {
  if (!hasText(config.prompt)) {
    throw new Error(QUICK_PROMPT_PROMPT_REQUIRED_ERROR);
  }

  if (!isQuickPromptTargetAvailable()) {
    if (!hasText(config.agentId) || !hasText(config.apiUrl)) {
      throw new Error(QUICK_PROMPT_REQUIRED_ERROR);
    }

    if (hasWidgetDom()) {
      clearState();
    }

    await load(
      {
        agentId: config.agentId,
        apiUrl: config.apiUrl,
      },
      { suppressInitialCallout: true },
    );
  }

  if (!isQuickPromptTargetAvailable()) {
    throw new Error(QUICK_PROMPT_WIDGET_UNAVAILABLE_ERROR);
  }

  await waitForQuickPromptReady();
  openWidgetIfClosed();
  postQuickPrompt(config.prompt);
}

function reset(): void {
  clearState();
}

declare global {
  interface Window {
    ResidentAgentWidget?: {
      load: (config: Partial<WidgetConfig>) => Promise<void>;
      quickPromptAsync: (config?: Partial<QuickPromptConfig>) => Promise<void>;
      openConversationAsync: (config: OpenConversationConfig) => Promise<void>;
      reset: () => void;
    };
  }
}

if (typeof window !== "undefined") {
  window.ResidentAgentWidget = {
    load,
    quickPromptAsync,
    openConversationAsync,
    reset,
  };
}

export { load, quickPromptAsync, openConversationAsync, reset };
